import { useState, useEffect, useContext } from "react";
import "./formStyle.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import { z, ZodType } from "zod";
import Cookies from "js-cookie";
import axios from "axios";

// context
import { AuthContext } from "../../contexts/authContext";

// icon
import {
  userIcon,
  passwordIcon,
  visibleIcon,
  invisibleIcon,
  checkboxEmptyIcon,
  checkboxCheckedIcon,
} from "../../constants/icons";

// utils
import monitorInputChange from "../../utils/monitorInputChange";

// interface
import { ErrorParams } from "../../interfaces/errorParams";
interface LoginData {
  email: string;
  password: string;
}

type NotificationType = "success" | "info" | "warning" | "error";


const API_URL = "/api/v1";
const Signin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const status = searchParams.get("status");
  let ivtoken = Cookies.get("ivtoken") || null;
  const { loginUser } = useContext(AuthContext);
  const [api, contextHolder] = notification.useNotification();
  const [formdata, setFormdata] = useState<LoginData>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [remember, setRemember] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorParams, setErrorParams] = useState<ErrorParams[]>([]);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };

  const loginSchema: ZodType<LoginData> = z.object({
    email: z.string().email(),
    password: z.string().min(1).max(100),
  });

  const handleValidation = () => {
    let errorlist: any[] = [];
    const validation = loginSchema.safeParse(formdata);
    if (!validation.success) {
      const { errors } = validation.error;
      errors.forEach((err) => {
        errorlist.push({
          path: err.path[0],
          message: err.message,
        });
      });
    }
    setErrorParams(errorlist);

    if (errorlist.length > 0) {
      return false;
    } else {
      return true;
    }
  };

  const handleUserLogin = async () =>{
    try {
      setLoading(true);
      const validate = handleValidation();
      if(!validate){
        setLoading(false);
        return false;
      }

      const res = await loginUser(formdata);
      if(res.success === false && res.access === false){
        setLoading(false);
        opNotification("error", res.error, res.message);
      }else{
        if(ivtoken !== null){
          const ivres = await axios.get(
            `${API_URL}/invite/verify?token=${ivtoken}`
          );
          if (ivres.status === 200 && ivres.data.success){
            Cookies.remove("ivtoken")
            navigate('/dashboard/evaluation')
          }
        }
        navigate('/dashboard/evaluation')

      }

    } catch (error:any) {
      console.log("Error logging in:", error)
    }finally{
      setLoading(false)
    }
  }

  useEffect(() => {
    if (action !== null) {
      if (action === "create" && status === "success") {
        opNotification(
          "success",
          "Account Created",
          "Account created successfully."
        );
      }
      else if(action === "rpwreq" && status === "success"){
        opNotification(
          "success",
          "Password Reset Requested",
          "A password reset link has been sent to your email."
        );
      }
      else if(action === "rpwrst" && status === "success"){
        opNotification(
          "success",
          "Password Reset Success",
          "Your password has been reset successfully!"
        );
      }
    }
    // eslint-disable-next-line
  }, [action, status]);

  useEffect(() => {
    if (errorParams.length > 0) {
      errorParams.forEach((err) => {
        let ele = document.getElementById(err.path);
        if (ele) {
          ele.style.borderColor = "#FF7875";
        }
      });
    }
    // eslist-disable-next-line
  }, [errorParams]);

  return (
    <div className="formContainer">
      {contextHolder}
      <span className="title">Log-in to have access to your evaluations</span>
      <form
        className="formHolder"
        onSubmit={(e) => {
          e.preventDefault();
          handleUserLogin();
        }}
      >
        <div className="inputHolder" id="email">
          <div className="iconHolder">{userIcon}</div>
          <input
            type="email"
            value={formdata.email}
            onChange={(e) => {
              setFormdata({
                ...formdata,
                email: e.target.value,
              });
              monitorInputChange("email");
            }}
            placeholder="E-mail"
          />
        </div>
        <div className="inputHolder" id="password">
          <div className="iconHolder">{passwordIcon}</div>
          <input
            type={showPassword ? "text" : "password"}
            value={formdata.password}
            onChange={(e) => {
              setFormdata({
                ...formdata,
                password: e.target.value,
              });
              monitorInputChange("password");
            }}
            placeholder="Password"
          />
          <div
            className="iconHolder toggle"
            onClick={() => {
              setShowPassword((prev) => !prev);
            }}
          >
            {showPassword ? invisibleIcon : visibleIcon}
          </div>
        </div>

        <div className="actionHolder">
          <span className="checkboxHolder">
            <div
              className={`iconHolder ${remember ? "active" : ""}`}
              onClick={() => {
                setRemember((prev) => !prev);
              }}
            >
              {remember ? checkboxCheckedIcon : checkboxEmptyIcon}
            </div>
            <span>Remember me</span>
          </span>
          <span className="forgetPassword"
            onClick={()=>{
              navigate('/auth/resetpwrequest')
            }}
          >Forgot password?</span>
        </div>

        <button
          type="submit"
          className="submit"
          disabled={loading ? true : false}
        >
          {loading ? "Logging in...": "Log in"}
        </button>

        <div className="formfooter">
          <span>Or</span>
          <span
            className="link"
            onClick={() => {
              navigate("/auth/signup");
            }}
          >
            Register now!
          </span>
        </div>
      </form>
    </div>
  );
};

export default Signin;
